import { useSession } from "next-auth/react";
import { useMemo } from "react";

import { useIsEmbed } from "@calcom/embed-core/embed-iframe";
import UnconfirmedBookingBadge from "@calcom/features/bookings/UnconfirmedBookingBadge";
import {
  useOrgBranding,
  type OrganizationBranding,
} from "@calcom/features/ee/organizations/context/provider";
import { KBarTrigger } from "@calcom/features/kbar/Kbar";
import { AiAssistantSsoService } from "@calcom/lib/agents/aiAssistant/aiAssistantSsoService";
import classNames from "@calcom/ui/classNames";

import { TeamInviteBadge } from "../TeamInviteBadge";
import type { NavigationItemType } from "./NavigationItem";
import { NavigationItem, MobileNavigationItem, MobileNavigationMoreItem } from "./NavigationItem";
import { UserPermissionRole } from "../../../prisma/enums";

export const MORE_SEPARATOR_NAME = "more";

const getNavigationItems = (orgBranding: OrganizationBranding): NavigationItemType[] => [
  {
    name: "Home",
    href: "/dashboard",
    icon: "layout-dashboard" as const,
    customIcon: true,
    role: "all",
    userType: "all",
    userCategory: "all",
    // customIconElement: (
    //   <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    //     <g clip-path="url(#clip0_385_15248)">
    //       <path
    //         d="M6 15.3353V8.66862H10V15.3353M2 6.66862L8 2.00195L14 6.66862V14.002C14 14.3556 13.8595 14.6947 13.6095 14.9448C13.3594 15.1948 13.0203 15.3353 12.6667 15.3353H3.33333C2.97971 15.3353 2.64057 15.1948 2.39052 14.9448C2.14048 14.6947 2 14.3556 2 14.002V6.66862Z"
    //         stroke="#FAFBFF"
    //         stroke-width="1.5"
    //         stroke-linecap="round"
    //         stroke-linejoin="round"
    //       />
    //     </g>
    //     <defs>
    //       <clipPath id="clip0_385_15248">
    //         <rect width="16" height="16" fill="white" transform="translate(0 0.668701)" />
    //       </clipPath>
    //     </defs>
    //   </svg>
    // ),
  },
  // {
  //   name: "ai_assistant",
  //   href: "#",
  //   icon: "sparkles" as const,
  //   onClick: async (e: React.MouseEvent) => {
  //     e.preventDefault();
  //     try {
  //       const ssoService = new AiAssistantSsoService();
  //       const redirectUrl = await ssoService.getRedirectUrl();
  //       window.open(redirectUrl, "_blank");
  //     } catch (error) {
  //       console.error("Failed to redirect to AI Assistant:", error);
  //       // You might want to show a toast notification here
  //     }
  //   },
  // },
  {
    name: "Appointments",
    href: "/bookings",
    icon: "calendar" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
    badge: <UnconfirmedBookingBadge />,
    isCurrent: ({ pathname }: { pathname: string | null }) => pathname?.startsWith("/bookings") ?? false,
  },
  {
    name: "Patients",
    href: "/my-patients",
    icon: "user-plus" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
  },
  {
    name: "Consultations",
    href: "/consultations",
    icon: "video" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
    isCurrent: ({ pathname }: { pathname: string | null }) => pathname?.startsWith("/consultations") ?? false,
  },
  // {
  //   name: "Availability",
  //   href: "/availability",
  //   icon: "calendar" as const,
  //   role: "all",
  // },
  {
    name: "My Calendar",
    href: "/my-calendar",
    icon: "calendar" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
  },
  {
    name: "event_types_page_title",
    href: "/event-types",
    icon: "link" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
  },
  {
    name: "My AI Assistants",
    href: "/assistant",
    icon: "users" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
  },
  {
    name: "Prescriptions",
    href: "/prescriptions",
    icon: "file-text" as const,
    role: "admin",
    userType: "all",
    userCategory: "PHARMACY",
    isCurrent: ({ pathname }: { pathname: string | null }) => pathname?.startsWith("/prescriptions") ?? false,
  },
  {
    name: "Invoices",
    href: "/invoices",
    icon: "clipboard-check" as const,
    role: "all",
    userType: "all",
    userCategory: "all",
    isCurrent: ({ pathname }: { pathname: string | null }) => pathname?.startsWith("/invoices") ?? false,
  },
  // {
  //   name: "Teams",
  //   href: "/teams",
  //   icon: "users" as const,
  //   badge: <TeamInviteBadge />,
  //   moreOnMobile: true,
  // },
  // {
  //   name: "my_patients",
  //   href: "/teams",
  //   icon: "user-plus" as const,
  //   badge: <TeamInviteBadge />,
  //   moreOnMobile: true,
  // },
  // ...(orgBranding
  //   ? [
  //       {
  //         name: "members",
  //         href: `/settings/organizations/${orgBranding.slug}/members`,
  //         icon: "building" as const,
  //         moreOnMobile: true,
  //       } satisfies NavigationItemType,
  //     ]
  //   : []),
  {
    name: "teams",
    href: "/teams",
    icon: "users",
    badge: <TeamInviteBadge />,
    moreOnMobile: true,
    role: "admin",
    userType: "all",
    userCategory: "all",
  },
  //  {
  //           name: "routing",
  //           href: "/routing",
  //           icon: "split",
  //    role: "admin",
  //           isCurrent: ({ pathname }) => pathname?.startsWith("/routing") ?? false,
  //           moreOnMobile: true,
  //         },
  {
    name: "workflows",
    href: "/workflows",
    icon: "zap",
    role: "admin",
    userType: "all",
    userCategory: "all",
    moreOnMobile: true,
  },
  ...(orgBranding
    ? [
      {
        name: "apps",
        href: "/apps",
        icon: "grid-3x3",
        role: "admin",
        userType: "all",
        userCategory: "all",
        moreOnMobile: true,
        isCurrent: ({ pathname: path, item }) => {
          // During Server rendering path is /v2/apps but on client it becomes /apps(weird..)
          return (path?.startsWith(item.href) ?? false) && !(path?.includes("routing-forms/") ?? false);
        },
        child: [
          {
            name: "app_store",
            href: "/apps",
            userType: "all",
            userCategory: "all",
            isCurrent: ({ pathname: path, item }) => {
              // During Server rendering path is /v2/apps but on client it becomes /apps(weird..)
              return (
                (path?.startsWith(item.href) ?? false) &&
                !(path?.includes("routing-forms/") ?? false) &&
                !(path?.includes("/installed") ?? false)
              );
            },
          },
          {
            name: "installed_apps",
            href: "/apps/installed/calendar",
            userType: "all",
            userCategory: "all",
            isCurrent: ({ pathname: path }) =>
              (path?.startsWith("/apps/installed/") ?? false) ||
              (path?.startsWith("/v2/apps/installed/") ?? false),
          },
        ],
      } satisfies NavigationItemType,
    ]
    : []),
  // ...(orgBranding?.role !== "MEMBER"
  //   ? [
  //       {
  //         name: "apps",
  //         href: "/apps",
  //         icon: "grid-3x3" as const,
  //         moreOnMobile: true,
  //         isCurrent: ({
  //           pathname: path,
  //           item,
  //         }: {
  //           pathname: string | null;
  //           item: Pick<NavigationItemType, "href">;
  //         }) => {
  //           // During Server rendering path is /v2/apps but on client it becomes /apps(weird..)
  //           return (path?.startsWith(item.href) ?? false) && !(path?.includes("routing-forms/") ?? false);
  //         },
  //         child: [
  //           {
  //             name: "app_store",
  //             href: "/apps",
  //             isCurrent: ({
  //               pathname: path,
  //               item,
  //             }: {
  //               pathname: string | null;
  //               item: Pick<NavigationItemType, "href">;
  //             }) => {
  //               // During Server rendering path is /v2/apps but on client it becomes /apps(weird..)
  //               return (
  //                 (path?.startsWith(item.href) ?? false) &&
  //                 !(path?.includes("routing-forms/") ?? false) &&
  //                 !(path?.includes("/installed") ?? false)
  //               );
  //             },
  //           },
  //           {
  //             name: "installed_apps",
  //             href: "/apps/installed/calendar",
  //             isCurrent: ({ pathname: path }: { pathname: string | null }) =>
  //               (path?.startsWith("/apps/installed/") ?? false) ||
  //               (path?.startsWith("/v2/apps/installed/") ?? false),
  //           },
  //         ],
  //       },
  //     ]
  //   : []),
  {
    name: MORE_SEPARATOR_NAME,
    href: "/more",
    icon: "ellipsis" as const,
    userType: "all",
    userCategory: "all",
  },
  // ...(orgBranding?.role === "OWNER"
  //   ? [
  //       {
  //         name: "routing",
  //         href: "/routing",
  //         icon: "split" as const,
  //         isCurrent: ({ pathname }: { pathname: string | null }) => pathname?.startsWith("/routing") ?? false,
  //         moreOnMobile: true,
  //       },
  //     ]
  //   : []),
  // ...(orgBranding?.role === "OWNER"
  //   ? [
  //       {
  //         name: "workflows",
  //         href: "/workflows",
  //         icon: "zap" as const,
  //         moreOnMobile: true,
  //       },
  //     ]
  //   : []),
  {
    name: "Reports",
    href: "/insights",
    icon: "chart-bar" as const,
    userType: "all",
    userCategory: "all",
    isCurrent: ({
      pathname: path,
      item,
    }: {
      pathname: string | null;
      item: Pick<NavigationItemType, "href">;
    }) => path?.startsWith(item.href) ?? false,
    moreOnMobile: true,
    child: [
      {
        name: "bookings",
        href: "/insights",
        userType: "all",
        userCategory: "all",
        isCurrent: ({ pathname: path }: { pathname: string | null }) => path === "/insights",
      },
      {
        name: "routing",
        href: "/insights/routing",
        role: "admin",
        userType: "all",
        userCategory: "all",
        isCurrent: ({ pathname: path }: { pathname: string | null }) =>
          path?.startsWith("/insights/routing") ?? false,
      },
      {
        name: "router_position",
        href: "/insights/router-position",
        role: "admin",
        userType: "all",
        userCategory: "all",
        isCurrent: ({ pathname: path }) => path?.startsWith("/insights/router-position") ?? false,
      },
    ],
  },
  {
    name: "Inventory",
    href: "/inventory",
    icon: "trello" as const,
    role: "admin",
    userType: "all",
    userCategory: "PHARMACY",
    isCurrent: ({
      pathname: path,
      item,
    }: {
      pathname: string | null;
      item: Pick<NavigationItemType, "href">;
    }) => path?.startsWith(item.href) ?? false,
    moreOnMobile: true,

  },


];

const platformNavigationItems: NavigationItemType[] = [
  {
    name: "Dashboard",
    href: "/settings/platform/",
    icon: "layout-dashboard" as const,
  },
  {
    name: "Documentation",
    href: "https://docs.cal.com/docs/platform",
    icon: "chart-bar" as const,
    target: "_blank",
  },
  {
    name: "API reference",
    href: "https://api.cal.com/v2/docs#/",
    icon: "terminal" as const,
    target: "_blank",
  },
  {
    name: "Atoms",
    href: "https://docs.cal.com/docs/platform#atoms",
    icon: "atom" as const,
    target: "_blank",
  },
  {
    name: MORE_SEPARATOR_NAME,
    href: "https://docs.cal.com/docs/platform/faq",
    icon: "ellipsis" as const,
    target: "_blank",
  },
  {
    name: "Billing",
    href: "/settings/platform/billing",
    icon: "credit-card" as const,
    moreOnMobile: true,
  },
  {
    name: "Members",
    href: "/settings/platform/members",
    icon: "users" as const,
    moreOnMobile: true,
  },
  {
    name: "Managed Users",
    href: "/settings/platform/managed-users",
    icon: "users" as const,
    moreOnMobile: true,
  },
];

/**
 * Helper function to check if a navigation item should be visible based on user's role, userType, and userCategory
 * Uses OR logic: item is visible if role matches OR userType matches OR userCategory matches
 */
const shouldShowNavigationItem = (
  item: NavigationItemType,
  userRole: string | undefined,
  userType: string | undefined,
  userCategory: string | undefined
): boolean => {
  // Check role (defaults to "all" if not specified)
  const itemRole = item.role || "all";
  let roleMatches = false;
  if (itemRole === "all") {
    roleMatches = true;
  } else if (itemRole === "admin") {
    const isAdmin = userRole?.includes("ADMIN");
    roleMatches = isAdmin || false;
  }

  // Check userType (defaults to "all" if not specified)
  const itemUserType = item.userType || "all";
  const userTypeMatches = itemUserType === "all" || itemUserType === userType;

  // Check userCategory (defaults to "all" if not specified)
  const itemUserCategory = item.userCategory || "all";
  const userCategoryMatches = itemUserCategory === "all" || itemUserCategory === userCategory;


  // Item is visible if ANY condition matches (OR logic)
  return roleMatches || userTypeMatches || userCategoryMatches;
};

const useNavigationItems = (isPlatformNavigation = false) => {
  const orgBranding = useOrgBranding();
  return useMemo(() => {
    const items = !isPlatformNavigation ? getNavigationItems(orgBranding) : platformNavigationItems;

    const desktopNavigationItems = items.filter((item) => item.name !== MORE_SEPARATOR_NAME);
    const mobileNavigationBottomItems = items.filter(
      (item) => (!item.moreOnMobile && !item.onlyDesktop) || item.name === MORE_SEPARATOR_NAME
    );
    const mobileNavigationMoreItems = items.filter(
      (item) => item.moreOnMobile && !item.onlyDesktop && item.name !== MORE_SEPARATOR_NAME
    );

    return { desktopNavigationItems, mobileNavigationBottomItems, mobileNavigationMoreItems };
  }, [isPlatformNavigation, orgBranding]);
};

export const Navigation = ({
  isPlatformNavigation = false,
  isCollapsed = false
}: {
  isPlatformNavigation?: boolean;
  isCollapsed?: boolean;
}) => {
  const session = useSession();
  const { desktopNavigationItems } = useNavigationItems(isPlatformNavigation);
  const userRole = session.data?.user.role;
  const userType = session.data?.user.userType;
  const userCategory = session.data?.user.userCategory;

  // Filter items based on role, userType, and userCategory
  let filteredDesktopNavigationItems = desktopNavigationItems.filter((item) =>
    shouldShowNavigationItem(item, userRole, userType, userCategory)
  );

  // if(!isAdmin) {
  //   filteredDesktopNavigationItems = filteredDesktopNavigationItems.map((item)=>{
  //     if(item?.child){
  //       item?.child = item?.child.filter((sub) => sub?.role !== 'admin')
  //     }
  //   })
  // }

  // Filter out items for platform practitioners with bookingEngineEnabled=false
  const isPlatformPractitioner = session.data?.user.userCategory === "PRACTITIONER" && session.data?.user.userType === "PLATFORM";
  const shouldHideItems = isPlatformPractitioner && session.data?.user.bookingEngineEnabled === false;

  if (shouldHideItems) {
    const itemsToHide = ["Home", "Appointments", "Patients", "My Calendar", "event_types_page_title", "Reports", "Invoices"];
    filteredDesktopNavigationItems = filteredDesktopNavigationItems.filter((item) => !itemsToHide.includes(item.name));
  }

  // Hide appointments, consultations, patients, event types for pharmacy users (desktop)
  if (userCategory === "PHARMACY") {
    const pharmacyHiddenItems = ["Appointments", "Consultations", "Patients", "event_types_page_title","Invoices"];
    filteredDesktopNavigationItems = filteredDesktopNavigationItems.filter((item) => !pharmacyHiddenItems.includes(item.name));
  }

  // Conditionally change "My AI Assistants" to "My Care Suite" based on bookingEngineEnabled
  filteredDesktopNavigationItems = filteredDesktopNavigationItems.map((item) => {
    if (item.name === "My AI Assistants") {
      return {
        ...item,
        name: shouldHideItems ? "My Care Suite" : "My AI Assistants",
      };
    }
    return item;
  });

  return (
    <nav className="mt-2 flex-1 md:px-2 lg:mt-4 lg:px-0">
      {filteredDesktopNavigationItems.map((item) => (
        <NavigationItem key={item.name} item={item} isCollapsed={isCollapsed} showAdminOnly={item.role == 'admin'} />
      ))}
      <div className="text-subtle mt-0.5 lg:hidden">
        <KBarTrigger />
      </div>
    </nav>
  );
};

export function MobileNavigationContainer({
  isPlatformNavigation = false,
}: {
  isPlatformNavigation?: boolean;
}) {
  const { status } = useSession();
  if (status !== "authenticated") return null;
  return <MobileNavigation isPlatformNavigation={isPlatformNavigation} />;
}

const MobileNavigation = ({ isPlatformNavigation = false }: { isPlatformNavigation?: boolean }) => {
  const isEmbed = useIsEmbed();
  const session = useSession();
  const { mobileNavigationBottomItems } = useNavigationItems(isPlatformNavigation);

  // Filter items based on role, userType, and userCategory
  const userRole = session.data?.user.role;
  const userType = session.data?.user.userType;
  const userCategory = session.data?.user.userCategory;
  let filteredMobileNavigationBottomItems = mobileNavigationBottomItems.filter((item) =>
    shouldShowNavigationItem(item, userRole, userType, userCategory)
  );

  // Filter out items for platform practitioners with bookingEngineEnabled=false
  const isPlatformPractitioner = session.data?.user.userCategory === "PRACTITIONER" && session.data?.user.userType === "PLATFORM";
  const shouldHideItems = isPlatformPractitioner && session.data?.user.bookingEngineEnabled === false;

  if (shouldHideItems) {
    const itemsToHide = ["Home", "Appointments", "Patients", "My Calendar", "event_types_page_title", "Reports", "Prescriptions", "Invoices", MORE_SEPARATOR_NAME];

    filteredMobileNavigationBottomItems = filteredMobileNavigationBottomItems.filter((item) => !itemsToHide.includes(item.name));
  }

  // Hide appointments, consultations, patients, event types for pharmacy users (mobile bottom)
  if (userCategory === "PHARMACY") {
    const pharmacyHiddenItems = ["Appointments", "Consultations", "Patients", "event_types_page_title","Invoices"];
    filteredMobileNavigationBottomItems = filteredMobileNavigationBottomItems.filter((item) => !pharmacyHiddenItems.includes(item.name));
  }

  // Conditionally change "My AI Assistants" to "My Care Suite" based on bookingEngineEnabled
  filteredMobileNavigationBottomItems = filteredMobileNavigationBottomItems.map((item) => {
    if (item.name === "My AI Assistants") {
      return {
        ...item,
        name: shouldHideItems ? "My Care Suite" : "My AI Assistants",
      };
    }
    return item;
  });

  return (
    <>
      <nav
        className={classNames(
          "pwa:pb-[max(0.25rem,env(safe-area-inset-bottom))] pwa:-mx-2 bg-muted border-subtle fixed bottom-0 left-0 z-30 flex w-full border-t bg-opacity-40 px-1 shadow backdrop-blur-md md:hidden",
          isEmbed && "hidden"
        )}>
        {filteredMobileNavigationBottomItems.map((item) => (
          <MobileNavigationItem key={item.name} item={item} />
        ))}
      </nav>
      {/* add padding to content for mobile navigation*/}
      <div className="block pt-12 md:hidden" />
    </>
  );
};

export const MobileNavigationMoreItems = () => {
  const session = useSession();
  const { mobileNavigationMoreItems } = useNavigationItems();

  // Filter items based on role, userType, and userCategory
  const userRole = session.data?.user.role;
  const userType = session.data?.user.userType;
  const userCategory = session.data?.user.userCategory;
  let filteredMobileNavigationMoreItems = mobileNavigationMoreItems.filter((item) =>
    shouldShowNavigationItem(item, userRole, userType, userCategory)
  );

  // Filter out items for platform practitioners with bookingEngineEnabled=false
  const isPlatformPractitioner = session.data?.user.userCategory === "PRACTITIONER" && session.data?.user.userType === "PLATFORM";
  const shouldHideItems = isPlatformPractitioner && session.data?.user.bookingEngineEnabled === false;

  if (shouldHideItems) {
    const itemsToHide = ["Home", "Appointments", "Patients", "My Calendar", "event_types_page_title", "Reports", "Prescriptions", "Invoices"];
    filteredMobileNavigationMoreItems = filteredMobileNavigationMoreItems.filter((item) => !itemsToHide.includes(item.name));
  }

  // Hide appointments, consultations, patients, event types for pharmacy users (mobile more)
  if (userCategory === "PHARMACY") {
    const pharmacyHiddenItems = ["Appointments", "Consultations", "Patients", "event_types_page_title","Invoices"];
    filteredMobileNavigationMoreItems = filteredMobileNavigationMoreItems.filter((item) => !pharmacyHiddenItems.includes(item.name));
  }

  // Conditionally change "My AI Assistants" to "My Care Suite" based on bookingEngineEnabled
  filteredMobileNavigationMoreItems = filteredMobileNavigationMoreItems.map((item) => {
    if (item.name === "My AI Assistants") {
      return {
        ...item,
        name: shouldHideItems ? "My Care Suite" : "My AI Assistants",
      };
    }
    return item;
  });

  return (
    <ul className="border-subtle mt-2 rounded-md border">
      {filteredMobileNavigationMoreItems.map((item) => (
        <MobileNavigationMoreItem key={item.name} item={item} />
      ))}
    </ul>
  );
};
